import { AxiosError } from 'axios'
import * as Sentry from '@sentry/react'
import { isAbortOrCancelError } from '@/shared/lib/is-abort-or-cancel-error'

export type ClientFailureSeverity = 'crash' | 'failed_action'

export type ClientFailureSurface =
  | 'lex'
  | 'ppwr-supplier-portal'
  | 'supplier-portal'

const reportedErrors = new WeakSet<object>()

let clientFailureSurface: ClientFailureSurface | null = null

export function setClientFailureSurface(surface: ClientFailureSurface): void {
  clientFailureSurface = surface
}

export function getClientFailureSurface(): ClientFailureSurface | null {
  return clientFailureSurface
}

const EXPECTED_HTTP_STATUSES = new Set([400, 401, 403, 409, 422, 304])

export function isExpectedClientFailure(error: unknown): boolean {
  if (isAbortOrCancelError(error)) {
    return true
  }

  if (error instanceof AxiosError) {
    const status = error.response?.status
    if (status != null && EXPECTED_HTTP_STATUSES.has(status)) {
      return true
    }
  }

  return false
}

function isSentryInitialized(): boolean {
  return Sentry.getClient() != null
}

function markErrorReported(error: unknown): boolean {
  if (error == null || typeof error !== 'object') {
    return false
  }

  if (reportedErrors.has(error)) {
    return true
  }

  reportedErrors.add(error)
  return false
}

export function reportUnexpectedClientFailure(args: {
  error: unknown
  severity: ClientFailureSeverity
}): void {
  if (!isSentryInitialized()) {
    return
  }

  if (isExpectedClientFailure(args.error)) {
    return
  }

  if (markErrorReported(args.error)) {
    return
  }

  const surface = clientFailureSurface ?? 'unknown'
  const route =
    typeof window !== 'undefined' ? window.location.pathname : 'unknown'

  Sentry.captureException(args.error, {
    tags: {
      severity: args.severity,
      surface,
      route,
    },
  })
}

export function setBrowserSentryUserContext(args: {
  userId?: string | null
  organizationId?: string | null
}): void {
  if (!isSentryInitialized()) {
    return
  }

  Sentry.setUser({ id: args.userId ?? 'unknown' })
  Sentry.setTag('organizationId', args.organizationId ?? 'unknown')
}

export function clearBrowserSentryUserContext(): void {
  if (!isSentryInitialized()) {
    return
  }

  Sentry.setUser(null)
  Sentry.setTag('organizationId', 'unknown')
}
